import { FC, useEffect, useMemo, useRef, useState } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import Handsontable from 'handsontable';
import { CellChange, ChangeSource } from 'pages/ListManagement/types';

type CellValue = Handsontable.CellValue;
import {
    PDP_INDICATOR_THRESHOLD_CRITERIA_WORDS,
    UNITS_CHARS,
    UNITS_UNITS,
    PDP_INDICATOR_THRESHOLD_CRITERIA_UNITS,
    FILTERED_INDICATOR_CRITERIA,
} from 'pages/ListManagement/const';
import { ICustomListData, SourceData } from 'pages/ListManagement/types';
import { arrayToKeyValue } from 'utils/arrays';
import { listAllowedEmpty, requireTitle } from './helpers/utils';
import { MPdpIndicatorTargetHeader, translateData, translateHeaders } from './helpers/HeadersList';

import Grid from '../Grid';
import { listValidator, numberValidator, PDPUnitValidator } from '../Validators';
import { AutoCompleteRenderer, CustomIntervalRenderer } from './helpers/customRenderers';
import CustomDropboxRenderer from './helpers/CustomDropboxRenderer';
import { IRetailer } from 'shared/types';
import { keyBy, keys } from 'lodash';
import { useIntl } from 'react-intl';

const frenchTranslations: { [key: string]: string } = {
    Allergens: 'Allergènes',
    Description: 'Description',
    Title: 'Libellé',
    Characters: 'Caractères',
    Words: 'Mots',
    Paragraphs: 'Paragraphes',
    Units: 'Unités',
    'Average ratings': 'Ratings moyen',
    'Number of ratings': 'Nombre de ratings',
    'Number of images': "Nombre d'images",
    'Number of videos': 'Nombre de vidéos',
    Ingredients: 'Ingrédients',
    'Bullet points': 'Bullet points',
    'Enhanced content': 'Enhanced content',
    VTO: 'VTO',
    'Number of reviews': 'Nombre de reviews',
    Nutritions: 'Nutritions',
};

export const MPdpIndicatorThresholdsV3: FC<{
    list: IList;
    toolId: number;
    retailers: string[];
    categoriesData: {
        id: number;
        name: string;
    }[];
    retailersData: IRetailer[];
    pdpFilters?: string[];
}> = ({ list, toolId, retailers, categoriesData, retailersData, pdpFilters }) => {
    const translatePdpFilters = (filters: string[], language: 'fr' | 'en'): string[] => {
        const englishTranslations: { [key: string]: string } = Object.fromEntries(
            Object.entries(frenchTranslations).map(([key, value]) => [value, key]),
        );
        const translationMap = language === 'fr' ? frenchTranslations : englishTranslations;

        return filters.map((filter) => translationMap[filter] || filter);
    };

    const intl = useIntl();
    const hotRef = useRef<React.ElementRef<typeof HotTable> | null>(null);
    const translatedHeaders = translateHeaders(MPdpIndicatorTargetHeader);
    const translatedWords = translateData(PDP_INDICATOR_THRESHOLD_CRITERIA_WORDS);
    const translatedAttUnits = translateData(PDP_INDICATOR_THRESHOLD_CRITERIA_UNITS);
    const description = translateData(['description'])[0];
    const translatedUnitArray = translateData(UNITS_UNITS);
    const translatedCharArray = translateData(UNITS_CHARS);
    const [formatedList, setFormatedList] = useState<IList>(list);

    useEffect(() => {
        setFormatedList(list);
    }, [list.name]);

    const retailersByName = keyBy(retailersData, 'name');
    const retailersById = keyBy(retailersData, 'id');
    const categoriesByName = keyBy(categoriesData, 'name');
    const categoriesById = keyBy(categoriesData, 'id');

    const categoriesNames = keys(categoriesByName).sort();

    const translatedUnits = [...translatedCharArray, ...translatedUnitArray];
    const mapCriteria: any = {};
    const mapIDToCriteria: any = {};
    const mapIDToUnits: any = {};
    const mapUnits: any = {};

    translatedUnits.forEach((tU, i) => {
        mapUnits[tU] = [...UNITS_CHARS, ...UNITS_UNITS][i].split('.')[2];
        mapIDToUnits[[...UNITS_CHARS, ...UNITS_UNITS][i].split('.')[2]] = tU;
    });

    translatedWords.forEach((tC, i) => {
        mapCriteria[tC] = PDP_INDICATOR_THRESHOLD_CRITERIA_WORDS[i].split('.')[2];
        mapIDToCriteria[PDP_INDICATOR_THRESHOLD_CRITERIA_WORDS[i].split('.')[2]] = tC;
    });

    translatedAttUnits.forEach((tC, i) => {
        mapCriteria[tC] = PDP_INDICATOR_THRESHOLD_CRITERIA_UNITS[i].split('.')[2];
        mapIDToCriteria[PDP_INDICATOR_THRESHOLD_CRITERIA_UNITS[i].split('.')[2]] = tC;
    });

    const sortedDynamicCriteria: ICustomListData[] = pdpFilters
        ? translatePdpFilters(pdpFilters, intl.locale as 'fr' | 'en').map((filter) => {
              return {
                  id: mapCriteria[filter],
                  name: filter,
              } as ICustomListData;
          })
        : [];

    const sortedCriteria: ICustomListData[] = [...translatedWords, ...translatedAttUnits]
        .map((translatedData) => {
            return {
                id: mapCriteria[translatedData] as string,
                name: translatedData,
            } as ICustomListData;
        })
        .filter(({ name }) => {
            return !FILTERED_INDICATOR_CRITERIA.includes(name);
        });

    const checkColumn = (
        hotRef: React.RefObject<React.ElementRef<typeof HotTable> | null>,
        row: number,
        column: number,
        values: string[],
    ) => {
        const hot = hotRef?.current?.hotInstance;
        hot?.setDataAtCell(row, column, values[0]);
        hot?.setCellMeta(row, column, 'type', 'dropdown');
        hot?.setCellMeta(row, column, 'source', values);
        hot?.render();
    };

    const retailerWithParagraph = retailersData.reduce(
        (prev: string[], curr) => ([67697, 119966, 67681].includes(curr.id) ? [...prev, curr.name] : prev),
        [],
    );

    const afterChangeAutoCompletePreselector = (
        changes: CellChange[] | null,
        source: ChangeSource,
        hotRef: React.RefObject<React.ElementRef<typeof HotTable> | null>,
    ) => {
        if (changes) {
            const COLUMN = changes[0][1];
            const hot = hotRef?.current?.hotInstance;
            const rowId = changes[0][0];
            const row = hot?.getDataAtRow(rowId);
            const retailer = row?.[0];
            const attribute = row?.[2];

            if (
                COLUMN === 2 &&
                PDP_INDICATOR_THRESHOLD_CRITERIA_UNITS.map((word) => word.split('.')[2]).includes(changes[0][3])
            ) {
                checkColumn(hotRef, rowId, (changes[0][1] as number) + 2, translatedUnitArray);
            } else if (/0|2/.test(`${COLUMN}`)) {
                if (retailerWithParagraph.includes(retailer) && attribute === description) {
                    checkColumn(hotRef, rowId, 4, translatedCharArray);
                } else {
                    checkColumn(hotRef, rowId, 4, translatedCharArray.slice(0, -1));
                }
            }
        }
    };

    const beforeDownload = (listToDownload: CellValue[][]) => {
        const keyValueRetailers = arrayToKeyValue(sortedCriteria, 'id');

        const newData = listToDownload.map((row) => {
            const newRow = [...row];
            newRow[2] = keyValueRetailers[newRow[2] as string]?.name;
            return newRow;
        });

        return newData;
    };

    const beforeUpload = (listToUpload: SourceData) => {
        const keyValueRetailers = arrayToKeyValue(sortedCriteria, 'name');

        const uploadData = listToUpload.map((data) => {
            const newRow = [...data];
            newRow[2] = keyValueRetailers[newRow[2] as string]?.id;

            return newRow;
        });

        return uploadData;
    };

    const afterValidate = (listToValidate: IList) => {
        const newData = [...listToValidate.data].map((row) => {
            const newRow = [...row];

            if (row[0]) {
                newRow[0] = retailersByName[newRow[0] as string].id;
            }

            if (row[1]) {
                newRow[1] = categoriesByName[newRow[1] as string]?.id || 0;
            }

            newRow[3] = !row[3] ? null : Number(newRow[3]);

            if (row[4]) {
                newRow[4] = mapUnits[newRow[4] as string];
            }

            newRow[5] = !row[5] ? null : Number(newRow[5]);
            newRow[6] = !row[6] ? null : Number(newRow[6]);

            return newRow;
        });

        return { ...listToValidate, data: newData };
    };

    const afterBeginEditing = (rowId: number, column: number) => {
        const hot = hotRef?.current?.hotInstance;
        const row = hot?.getDataAtRow(rowId);

        if (row && column === 4) {
            const retailer = row?.[0];
            const attribute = row?.[2];

            if (PDP_INDICATOR_THRESHOLD_CRITERIA_UNITS.map((word) => word.split('.')[2]).includes(attribute)) {
                hot?.setCellMeta(rowId, 4, 'source', translatedUnitArray);
            } else if (retailerWithParagraph.includes(retailer) && attribute === description) {
                hot?.setCellMeta(rowId, 4, 'source', translatedCharArray);
            } else {
                hot?.setCellMeta(rowId, 4, 'source', translatedCharArray.slice(0, -1));
            }
        }
    };

    const categoriesWithEmpty = listAllowedEmpty(categoriesNames);

    const formatList = (l: IList) => {
        const newData = l.data.map((data) => {
            const newRow = [...data];
            newRow[4] = mapIDToUnits[newRow[4] as string];
            newRow[0] = retailersById[newRow[0] as string]?.name;
            newRow[1] = categoriesById[newRow[1] as string]?.name || '-';
            return newRow;
        });

        return {
            ...l,
            data: newData,
        };
    };

    const afterPaste = (data: any[], coords: any[]) => {
        const hot = hotRef?.current?.hotInstance;
        const newList = { ...formatedList };

        newList.data = data.map((row, i) => {
            const newRow = [...row];
            if (newRow[2]) {
                newRow[2] = newRow[2].replace(/\\n/g, '');
                hot?.setDataAtCell(i, 2, newRow[2]);
            }

            return newRow;
        });
        setFormatedList(newList);
    };

    const memoList = useMemo(() => formatList(formatedList), [formatedList.data, formatedList.name]);

    return (
        <Grid
            toolId={toolId}
            headers={MPdpIndicatorTargetHeader}
            list={memoList}
            paramHotRef={hotRef}
            afterPaste={afterPaste}
            afterBeginEditing={afterBeginEditing}
            beforeDownload={beforeDownload}
            beforeUpload={beforeUpload}
            afterChange={afterChangeAutoCompletePreselector}
            afterValidate={afterValidate}
        >
            <HotColumn
                validator={listValidator(retailers)}
                title={requireTitle(translatedHeaders[0])}
                type="dropdown"
                source={retailers}
            >
                <AutoCompleteRenderer list={retailers} hot-renderer />
            </HotColumn>
            <HotColumn
                validator={listValidator(categoriesWithEmpty)}
                title={requireTitle(translatedHeaders[1])}
                type="autocomplete"
                source={categoriesWithEmpty}
            >
                <AutoCompleteRenderer list={categoriesWithEmpty} hot-renderer />
            </HotColumn>
            <HotColumn title={requireTitle(translatedHeaders[2])}>
                <CustomDropboxRenderer
                    customList={sortedDynamicCriteria.length ? sortedDynamicCriteria : sortedCriteria}
                    hotInstance={hotRef}
                    hot-renderer
                />
            </HotColumn>
            <HotColumn
                type="text"
                validator={numberValidator(false, { min: 1 })}
                title={requireTitle(translatedHeaders[3])}
            >
                <CustomIntervalRenderer min={1} max={9999999999999} hot-renderer />
            </HotColumn>
            <HotColumn
                title={requireTitle(translatedHeaders[4])}
                validator={PDPUnitValidator(translatedUnits, false, translatedUnitArray)}
                type="dropdown"
            />
            <HotColumn
                type="text"
                validator={numberValidator(false, { min: 1 })}
                title={requireTitle(translatedHeaders[5])}
                allowEmpty={false}
            >
                <CustomIntervalRenderer allowEmpty={false} min={1} max={9999999999999} hot-renderer />
            </HotColumn>
            <HotColumn type="text" validator={numberValidator(true)} title={translatedHeaders[6]} />
        </Grid>
    );
};
